import axios from "axios";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GET_TESTSTATES } from "../../constant";
import { DataGrid } from "../index";
import { start, done } from "../../store/loaderSlice";
import { convertTimestamp } from "../../utils/convertTimestamp";
import { snackbarStart } from "../../store/SnackbarSlice";
import { useTranslation } from "react-i18next";
import { CSVLink } from "react-csv";
import BarChartIcon from "@mui/icons-material/BarChart";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

const DashboardTestResults = () => {
  const { token } = useSelector((state) => state.user);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [results, setResults] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  const fetchResults = async () => {
    dispatch(start());
    try {
      const { data } = await axios.get(GET_TESTSTATES, {
        headers: { "x-auth-token": token },
      });
      setResults(data);
    } catch (err) {
      console.error(err);
    } finally {
      dispatch(done());
    }
  };

  const handleDeleteResults = async () => {
    if (!selectedIds.length) return;
    dispatch(start());
    try {
      await axios.delete(GET_TESTSTATES, {
        headers: { "Content-Type": "application/json", "x-auth-token": token },
        data: { ids: selectedIds },
      });
      dispatch(
        snackbarStart({
          text: t("testsDashboard.deletedMessage"),
          severity: "success",
        }),
      );
      setSelectedIds([]);
      fetchResults();
    } catch {
      dispatch(
        snackbarStart({ text: t("somethingWentWrong"), severity: "error" }),
      );
    } finally {
      dispatch(done());
    }
  };

  const handleRecalculate = async () => {
    dispatch(start());
    try {
      await axios.post(
        `${GET_TESTSTATES}/recalculate`,
        { ids: selectedIds },
        {
          headers: {
            "Content-Type": "application/json",
            "x-auth-token": token,
          },
        },
      );
      dispatch(
        snackbarStart({
          text: t("recalculateSuccess") || "Recalculated successfully",
          severity: "success",
        }),
      );
      fetchResults();
    } catch {
      dispatch(
        snackbarStart({ text: t("somethingWentWrong"), severity: "error" }),
      );
    } finally {
      dispatch(done());
      setSelectedIds([]);
    }
  };

  const onSelectionChange = (ids) => setSelectedIds(ids);

  const columns = [
    { field: "id", headerName: "ID", width: 220 },
    {
      field: "userName",
      headerName: t("testsDashboard.name"),
      width: 180,
    },
    {
      field: "userEmail",
      headerName: t("testsDashboard.email"),
      width: 220,
    },
    {
      field: "percentage",
      headerName: t("testsDashboard.result"),
      width: 90,
    },
    {
      field: "totalQuestions",
      headerName: t("testsDashboard.totalQuestions"),
      width: 90,
    },
    {
      field: "correctAnswers",
      headerName: t("testsDashboard.correctAnswers"),
      width: 100,
    },
    {
      field: "incorrectAnswers",
      headerName: t("testsDashboard.incorrectAnswers"),
      width: 120,
    },
    {
      field: "unansweredQuestions",
      headerName: t("testsDashboard.unansweredQuestions"),
      width: 130,
    },
    {
      field: "startedAt",
      headerName: t("testsDashboard.startTime"),
      width: 150,
    },
    {
      field: "finishedAt",
      headerName: t("testsDashboard.endTime"),
      width: 150,
    },
  ];

  const rows = results.map((r) => ({
    id: r.testStateId,
    userName: r.userName,
    userEmail: r.userEmail,
    percentage: r.percentage,
    totalQuestions: r.totalQuestions,
    correctAnswers: r.correctAnswers,
    incorrectAnswers: r.incorrectAnswers,
    unansweredQuestions: r.unansweredQuestions,
    startedAt: convertTimestamp(r.startTime),
    finishedAt: convertTimestamp(r.endTime),
  }));

  const csvHeaders = columns.map(({ headerName, field }) => ({
    label: headerName,
    key: field,
  }));

  useEffect(() => {
    if (token) fetchResults();
  }, [token]);

  return (
    <section className="min-h-screen pt-24 pb-16 bg-gray-50 dark:bg-[#030712] transition-colors duration-300">
      <div className="container mx-auto px-4">
        {/* Page header */}
        <div className="flex items-center gap-4 mb-8 mt-2">
          <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center text-orange-500 flex-shrink-0">
            <BarChartIcon sx={{ fontSize: 26 }} />
          </div>
          <div>
            <h1 className="text-2xl font-black text-slate-800 dark:text-white tracking-tight">
              {t("dashboard.testResult")}
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {results.length} {t("total") || "total records"}
            </p>
          </div>
        </div>

        {/* Action toolbar */}
        <div className="flex flex-wrap gap-2.5 mb-5">
          <button
            onClick={handleDeleteResults}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white text-sm font-semibold transition-all shadow-sm"
          >
            <DeleteIcon sx={{ fontSize: 17 }} /> {t("delete")}
          </button>

          <button
            onClick={handleRecalculate}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-purple-500 hover:bg-purple-600 text-white text-sm font-semibold transition-all shadow-sm"
          >
            <RefreshIcon sx={{ fontSize: 17 }} /> {t("recalculate")}
          </button>

          <CSVLink
            data={rows}
            headers={csvHeaders}
            filename="test-results.csv"
            className="no-underline"
          >
            <button className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-white text-sm font-semibold transition-all">
              <FileDownloadIcon sx={{ fontSize: 17 }} /> {t("exportToCsv")}
            </button>
          </CSVLink>
        </div>

        {/* Data grid */}
        <div className="rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700/50 bg-white dark:bg-[#0f172a] shadow-sm">
          <DataGrid
            rows={rows}
            columns={columns}
            title=""
            checkboxSelection
            selectedIds={selectedIds}
            sx={{
              border: "none",
              "& .MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                outline: "none !important",
              },
            }}
            onRowsSelectionHandler={onSelectionChange}
          />
        </div>
      </div>
    </section>
  );
};

export default DashboardTestResults;
